import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import api, { ApiError } from '../utils/api';
import { formatTimeLabel } from '../constants/services';
import DatePills from '../components/public/DatePills';
import TimeSlots from '../components/public/TimeSlots';
import { confirm, notifyError, notifySuccess, toast } from '../utils/swal';

import './ManageBooking.css';

function formatHumanDate(iso) {
  if (!iso) return '';
  try {
    const [y, m, d] = iso.split('-').map((v) => parseInt(v, 10));
    const dt = new Date(y, m - 1, d);
    return dt.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

export default function ManageBooking() {
  const { appointmentId } = useParams();

  const [state, setState] = useState({
    status: 'loading',
    appointment: null,
    canModify: false,
    within24h: false,
    isActive: false,
    error: null,
  });

  // reschedule UI state (separate from state above)
  const [showReschedule, setShowReschedule] = useState(false);
  const [dates, setDates] = useState([]);
  const [datesLoading, setDatesLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedTime, setSelectedTime] = useState('');
  const [reschedSaving, setReschedSaving] = useState(false);
  const [reschedError, setReschedError] = useState('');
  const slotsAbortRef = useRef(null);

  const [cancelling, setCancelling] = useState(false);

  const load = useCallback(async () => {
    setState((s) => ({ ...s, status: 'loading', error: null }));
    try {
      const res = await api.get(`/api/manage/${encodeURIComponent(appointmentId)}`);
      setState({
        status: 'ready',
        appointment: res.appointment,
        canModify: !!res.canModify,
        within24h: !!res.within24h,
        isActive: !!res.isActive,
        error: null,
      });
    } catch (err) {
      setState({
        status: 'error',
        appointment: null,
        canModify: false,
        within24h: false,
        isActive: false,
        error:
          (err instanceof ApiError && err.status === 404)
            ? "We couldn't find that booking. Check the link in your email."
            : (err && err.message) || 'Could not load your booking.',
      });
    }
  }, [appointmentId]);

  useEffect(() => { load(); }, [load]);

  // ── reschedule: load weekday dates when modal opens ──
  useEffect(() => {
    if (!showReschedule) return;
    let cancelled = false;
    (async () => {
      setDatesLoading(true);
      try {
        const res = await api.get('/api/dates');
        if (cancelled) return;
        const list = (res && res.dates) || [];
        setDates(list);
        const match = state.appointment
          ? list.find((d) => d.iso === state.appointment.date)
          : null;
        setSelectedDate(match ? match.iso : (list[0]?.iso || ''));
      } catch (err) {
        if (!cancelled) console.error('[manage] dates load failed:', err);
      } finally {
        if (!cancelled) setDatesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [showReschedule, state.appointment]);

  // ── reschedule: load slots when date changes ──
  useEffect(() => {
    if (!showReschedule || !selectedDate) return;
    if (slotsAbortRef.current) slotsAbortRef.current.abort();
    const controller = new AbortController();
    slotsAbortRef.current = controller;

    setSlotsLoading(true);
    setSelectedTime('');
    setSlots([]);

    (async () => {
      try {
        const res = await api.get('/api/availability', {
          query: { date: selectedDate },
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;

        const a = state.appointment;
        const adjusted = ((res && res.slots) || []).map((s) => {
          if (a && selectedDate === a.date && s.time === a.time) {
            return { ...s, available: false, booked: true, isOwnSlot: true };
          }
          return s;
        });
        setSlots(adjusted);
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.error('[manage] availability load failed:', err);
      } finally {
        if (!controller.signal.aborted) setSlotsLoading(false);
      }
    })();
    return () => controller.abort();
  }, [showReschedule, selectedDate, state.appointment]);

  const openReschedule = () => {
    setReschedError('');
    setSelectedTime('');
    setShowReschedule(true);
  };

  const closeReschedule = () => {
    if (reschedSaving) return;
    setShowReschedule(false);
    setReschedError('');
  };

  const handleSaveReschedule = async () => {
    if (reschedSaving) return;
    setReschedError('');
    if (!selectedDate || !selectedTime) {
      setReschedError('Pick a new date and time.');
      return;
    }
    const a = state.appointment;
    if (selectedDate === a.date && selectedTime === a.time) {
      setReschedError("That's already your current appointment.");
      return;
    }

    setReschedSaving(true);
    try {
      const res = await api.patch(
        `/api/manage/${encodeURIComponent(appointmentId)}/reschedule`,
        { newDate: selectedDate, newTime: selectedTime }
      );
      setShowReschedule(false);
      notifySuccess(
        "You're all set.",
        `Your appointment is now ${formatHumanDate(res.appointment.date)} at ${formatTimeLabel(res.appointment.time)}.`
      );
      setState((s) => ({ ...s, appointment: res.appointment }));
    } catch (err) {
      if (err instanceof ApiError) {
        setReschedError(err.message);
      } else {
        setReschedError(err.message || 'Reschedule failed.');
      }
    } finally {
      setReschedSaving(false);
    }
  };

  const handleCancel = async () => {
    if (cancelling) return;

    const result = await confirm({
      title: 'Cancel your appointment?',
      text: "This can't be undone. We'll send a confirmation to your email.",
      confirmText: 'Yes, cancel',
      cancelText: 'Keep it',
      icon: 'warning',
    });
    if (!result.isConfirmed) return;

    setCancelling(true);
    try {
      const res = await api.post(`/api/manage/${encodeURIComponent(appointmentId)}/cancel`);
      setState((s) => ({
        ...s,
        appointment: res.appointment,
        canModify: false,
        isActive: false,
      }));
      toast('Your appointment has been cancelled.', 'success');
    } catch (err) {
      notifyError('Could not cancel', err.message || 'Please try again.');
    } finally {
      setCancelling(false);
    }
  };

  // ── render ──
  if (state.status === 'loading') {
    return (
      <div className="mb">
        <div className="mb__inner">
          <Link to="/" className="mb__brand display" aria-label="Bella 7 home">
            Bella <em>7</em>
          </Link>
          <div className="mb__card">
            <p className="mb__loading">Looking up your booking…</p>
          </div>
        </div>
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="mb">
        <div className="mb__inner">
          <Link to="/" className="mb__brand display" aria-label="Bella 7 home">
            Bella <em>7</em>
          </Link>
          <div className="mb__card">
            <span className="mono mb__kicker">Heads up</span>
            <h1 className="display mb__heading">
              Couldn't<br/><em>find that</em>.
            </h1>
            <p className="mb__intro">{state.error}</p>
            <Link to="/" className="mb__cta">
              <span>Back to home</span>
              <span className="mb__arrow">→</span>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const a = state.appointment;
  const niceDate = formatHumanDate(a.date);
  const niceTime = formatTimeLabel(a.time);
  const isCancelled = a.status === 'cancelled';
  const isCompleted = a.status === 'completed';
  const isMissing = a.status === 'missing';
  const canModify = state.canModify;
  const within24h = state.within24h;

  return (
    <div className="mb">
      <div className="mb__inner">
        <Link to="/" className="mb__brand display" aria-label="Bella 7 home">
          Bella <em>7</em>
        </Link>

        <div className="mb__card">
          <div className="mb__kicker-row">
            <span className="mono mb__kicker">
              {isCancelled ? 'Cancelled' : isCompleted ? 'Completed' : isMissing ? 'Missed' : 'Your booking'}
            </span>
            {!isCancelled && !isCompleted && !isMissing && (
              <span className={`mb__status-pill ${within24h ? 'mb__status-pill--warn' : 'mb__status-pill--ok'}`}>
                {within24h ? 'Within 24 hours' : 'Up to 24h before · changes welcome'}
              </span>
            )}
          </div>

          <h1 className="display mb__heading">
            Hi {a.firstName},<br />
            <em>here's your visit</em>.
          </h1>

          <div className="mb__details">
            <div className="mb__detail">
              <div className="mono mb__detail-label">Service</div>
              <div className="display mb__detail-value">{a.service}</div>
            </div>
            <div className="mb__detail">
              <div className="mono mb__detail-label">Date</div>
              <div className="display mb__detail-value">{niceDate}</div>
            </div>
            <div className="mb__detail">
              <div className="mono mb__detail-label">Time</div>
              <div className="display mb__detail-value">{niceTime}</div>
            </div>
          </div>

          <div className="mb__code">
            <div className="mono mb__code-label">Confirmation code</div>
            <div className="display mb__code-value">{a.appointmentId}</div>
          </div>

          {isCancelled && (
            <div className="mb__notice mb__notice--cancelled">
              <div className="display mb__notice-title">This appointment is cancelled.</div>
              <p>
                {a.cancellationReason || 'Cancelled as requested.'}{' '}
                We'd love to see you again whenever you're ready.
              </p>
              <Link to="/" className="mb__cta">
                <span>Book a new visit</span>
                <span className="mb__arrow">→</span>
              </Link>
            </div>
          )}

          {(isCompleted || isMissing) && (
            <div className="mb__notice mb__notice--past">
              <div className="display mb__notice-title">
                {isCompleted ? 'Thanks for visiting.' : 'This appointment has passed.'}
              </div>
              <p>
                {isCompleted
                  ? 'We hope you loved your visit.'
                  : 'No worries — you can always book a new time.'}
              </p>
              <Link to="/" className="mb__cta">
                <span>Book a new visit</span>
                <span className="mb__arrow">→</span>
              </Link>
            </div>
          )}

          {!isCancelled && !isCompleted && !isMissing && (
            <>
              {within24h ? (
                <div className="mb__notice mb__notice--warn">
                  <div className="display mb__notice-title">
                    We're less than 24 hours away.
                  </div>
                  <p>
                    To change or cancel within 24 hours, please reach out
                    directly — we'll help right away.
                  </p>
                  
                    <a
                      className="mb__cta"
                      href={`mailto:hello@bella7.com?subject=${encodeURIComponent('Help with my appointment ' + a.appointmentId)}`}
                    >
                      <span>Email us</span>
                      <span className="mb__arrow">→</span>
                    </a>
                </div>
              ) : (
                <div className="mb__actions">
                  <button
                    type="button"
                    className="mb__btn mb__btn--primary"
                    onClick={openReschedule}
                    disabled={!canModify}
                  >
                    <span>Reschedule</span>
                    <span className="mb__arrow">↻</span>
                  </button>
                  <button
                    type="button"
                    className="mb__btn mb__btn--danger"
                    onClick={handleCancel}
                    disabled={cancelling || !canModify}
                  >
                    {cancelling ? 'Cancelling…' : 'Cancel my appointment'}
                  </button>
                </div>
              )}
            </>
          )}

          <p className="mb__fineprint">
            Questions? Reply to your confirmation email or visit the
            <Link to="/#contact"> contact form</Link>.
          </p>
        </div>
      </div>

      {showReschedule && (
        <>
          <div className="mb-rm__scrim" onClick={closeReschedule} />
          <div
            className="mb-rm"
            role="dialog"
            aria-label="Reschedule your appointment"
            aria-modal="true"
          >
            <header className="mb-rm__head">
              <button
                type="button"
                className="mb-rm__close"
                onClick={closeReschedule}
                disabled={reschedSaving}
                aria-label="Close"
              >
                ×
              </button>
              <span className="mono mb-rm__kicker">Reschedule</span>
              <h2 className="display mb-rm__heading">Pick a new time.</h2>
              <div className="mb-rm__current">
                <span className="mono mb-rm__current-label">Currently</span>
                <span className="mb-rm__current-value">
                  {formatHumanDate(a.date).split(',')[0]} · {niceTime}
                </span>
              </div>
            </header>

            <div className="mb-rm__body">
              <div className="mb-rm__field">
                <label className="mono mb-rm__label">New date</label>
                <DatePills
                  dates={dates}
                  value={selectedDate}
                  onChange={(iso) => {
                    setSelectedDate(iso);
                    setReschedError('');
                  }}
                  loading={datesLoading}
                />
              </div>

              <div className="mb-rm__field">
                <label className="mono mb-rm__label">New time</label>
                <TimeSlots
                  slots={slots}
                  value={selectedTime}
                  onChange={(t) => {
                    setSelectedTime(t);
                    setReschedError('');
                  }}
                  loading={slotsLoading}
                />
              </div>

              {reschedError && (
                <div className="mb-rm__error" role="alert">
                  {reschedError}
                </div>
              )}

              <div className="mb-rm__actions">
                <button
                  type="button"
                  className="mb-rm__btn mb-rm__btn--ghost"
                  onClick={closeReschedule}
                  disabled={reschedSaving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="mb-rm__btn mb-rm__btn--primary"
                  onClick={handleSaveReschedule}
                  disabled={reschedSaving || !selectedDate || !selectedTime}
                >
                  {reschedSaving ? 'Saving…' : 'Confirm new time'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}